/**
 * Página de Configuración de Integración
 * Formulario completo para crear/editar una integración
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { integracionesAPI } from '../utils/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Loader2,
  Info,
  Eye,
  EyeOff,
  Play,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const IntegracionConfig = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { codigo } = useParams();
  const isEditing = codigo && codigo !== 'nueva';
  
  // Estados
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // Campos ocultos
  const [showTokenOauth, setShowTokenOauth] = useState(false);
  const [showClienteId, setShowClienteId] = useState(false);
  const [showSecretId, setShowSecretId] = useState(false);
  const [showTokenEntrada, setShowTokenEntrada] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Información general
    codigo: '',
    nombre: '',
    descripcion: '',
    logo_url: '',
    activo: true,
    orden: 0,
    
    // Parámetros de envío
    url_token_oauth: '',
    token_oauth: '',
    url_api_salida: '',
    metodo_http: 'POST',
    metodo_http_oauth: 'POST',
    cliente_id: '',
    secret_id: '',
    timeout: 30,
    reintentos: 3,
    parametros_operacion: '{}',
    json_oauth: '{}',
    json_envio: '{}',
    headers_adicionales: '{"Content-Type": "application/json"}',
    
    // Parámetros de recepción
    url_api_entrada: '',
    token_api_entrada: ''
  });
  
  // Valores de parámetros (detalle)
  const [valores, setValores] = useState([]);
  
  // Cargar datos si es edición
  useEffect(() => {
    if (isEditing) {
      loadIntegracion();
    } else {
      // Verificar si hay datos de clonación
      const cloneData = sessionStorage.getItem('integracion_clone');
      if (cloneData) {
        try {
          const data = JSON.parse(cloneData);
          setFormData({
            codigo: data.codigo || '',
            nombre: data.nombre || '',
            descripcion: data.descripcion || '',
            logo_url: data.logo_url || '',
            activo: data.activo !== false,
            orden: data.orden || 0,
            url_token_oauth: data.url_token_oauth || '',
            token_oauth: data.token_oauth || '',
            url_api_salida: data.url_api_salida || '',
            metodo_http: data.metodo_http || 'POST',
            metodo_http_oauth: data.metodo_http_oauth || 'POST',
            cliente_id: data.cliente_id || '',
            secret_id: data.secret_id || '',
            timeout: data.timeout || 30,
            reintentos: data.reintentos || 3,
            parametros_operacion: JSON.stringify(data.parametros_operacion || {}, null, 2),
            json_oauth: JSON.stringify(data.json_oauth || {}, null, 2),
            json_envio: JSON.stringify(data.json_envio || {}, null, 2),
            headers_adicionales: JSON.stringify(data.headers_adicionales || {"Content-Type": "application/json"}, null, 2),
            url_api_entrada: data.url_api_entrada || '',
            token_api_entrada: data.token_api_entrada || ''
          });
          
          if (data.valores && data.valores.length > 0) {
            setValores(data.valores.map(v => ({
              campo: v.campo,
              valor: v.valor || '',
              descripcion: v.descripcion || '',
              orden: v.orden || 0
            })));
          }
          
          sessionStorage.removeItem('integracion_clone');
        } catch (e) {
          console.error('Error parsing clone data:', e);
        }
      }
    }
  }, [isEditing, codigo]);
  
  const loadIntegracion = async () => {
    setLoading(true);
    try {
      const response = await integracionesAPI.get(codigo);
      const data = response.data;
      
      setFormData({
        codigo: data.codigo,
        nombre: data.nombre || '',
        descripcion: data.descripcion || '',
        logo_url: data.logo_url || '',
        activo: data.activo !== false,
        orden: data.orden || 0,
        url_token_oauth: data.url_token_oauth || '',
        token_oauth: data.token_oauth || '',
        url_api_salida: data.url_api_salida || '',
        metodo_http: data.metodo_http || 'POST',
        metodo_http_oauth: data.metodo_http_oauth || 'POST',
        cliente_id: data.cliente_id || '',
        secret_id: data.secret_id || '',
        timeout: data.timeout || 30,
        reintentos: data.reintentos || 3,
        parametros_operacion: JSON.stringify(data.parametros_operacion || {}, null, 2),
        json_oauth: JSON.stringify(data.json_oauth || {}, null, 2),
        json_envio: JSON.stringify(data.json_envio || {}, null, 2),
        headers_adicionales: JSON.stringify(data.headers_adicionales || {}, null, 2),
        url_api_entrada: data.url_api_entrada || '',
        token_api_entrada: data.token_api_entrada || ''
      });
      
      if (data.valores && data.valores.length > 0) {
        setValores(data.valores.map(v => ({
          id: v.id,
          campo: v.campo,
          valor: v.valor || '',
          descripcion: v.descripcion || '',
          orden: v.orden || 0
        })));
      }
    } catch (error) {
      toast.error('Error al cargar la integración');
      console.error(error);
      navigate('/integraciones');
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Validar JSON
  const isValidJson = (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };
  
  // Guardar
  const handleSave = async () => {
    // Validaciones
    if (!formData.codigo.trim()) {
      toast.error('El código es requerido');
      return;
    }
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.url_api_salida.trim()) {
      toast.error('La URL API de salida es requerida');
      return;
    }
    
    // Validar JSONs
    if (!isValidJson(formData.parametros_operacion)) {
      toast.error('Parámetros de operación: JSON inválido');
      return;
    }
    if (!isValidJson(formData.json_oauth)) {
      toast.error('JSON OAuth: JSON inválido');
      return;
    }
    if (!isValidJson(formData.json_envio)) {
      toast.error('JSON Envío: JSON inválido');
      return;
    }
    if (!isValidJson(formData.headers_adicionales)) {
      toast.error('Headers adicionales: JSON inválido');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        parametros_operacion: JSON.parse(formData.parametros_operacion),
        json_oauth: JSON.parse(formData.json_oauth),
        json_envio: JSON.parse(formData.json_envio),
        headers_adicionales: JSON.parse(formData.headers_adicionales)
      };
      
      if (isEditing) {
        await integracionesAPI.update(codigo, payload);
        
        // Actualizar valores
        if (valores.length > 0) {
          await integracionesAPI.updateValores(codigo, valores);
        }
        
        toast.success('Integración actualizada correctamente');
      } else {
        payload.valores = valores.map((v, idx) => ({
          campo: v.campo,
          valor: v.valor,
          descripcion: v.descripcion,
          orden: v.orden || idx
        }));
        
        await integracionesAPI.create(payload);
        toast.success('Integración creada correctamente');
        navigate('/integraciones');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar integración');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };
  
  // Probar conexión
  const handleTest = async () => {
    if (!isEditing) {
      toast.error('Guarda la integración primero para probar la conexión');
      return;
    }
    
    setTesting(true);
    try {
      const response = await integracionesAPI.probar(codigo);
      if (response.data.exito) {
        // Mostrar detalles si existen
        const detalles = response.data.detalles || [];
        if (detalles.length > 0) {
          toast.success(
            <div>
              <strong>{response.data.mensaje || 'Conexión exitosa'}</strong>
              <ul className="mt-1 text-xs">
                {detalles.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </div>,
            { duration: 5000 }
          );
        } else {
          toast.success(response.data.mensaje || 'Conexión exitosa');
        }
      } else {
        toast.error(response.data.error || 'Error de conexión');
      }
    } catch (error) {
      toast.error('Error al probar conexión');
      console.error(error);
    } finally {
      setTesting(false);
    }
  };
  
  // Agregar valor
  const addValor = () => {
    setValores(prev => [...prev, { campo: '', valor: '', descripcion: '', orden: prev.length }]);
  };
  
  // Eliminar valor
  const removeValor = (index) => {
    setValores(prev => prev.filter((_, i) => i !== index));
  };
  
  // Actualizar valor
  const updateValor = (index, field, value) => {
    setValores(prev => prev.map((v, i) => i === index ? { ...v, [field]: value } : v));
  };
  
  // Sincronizar valores con parámetros_operacion
  const syncValoresFromParams = () => {
    try {
      const params = JSON.parse(formData.parametros_operacion);
      const existingCamposMap = new Map(valores.map((v, idx) => [v.campo, idx]));
      
      const newValores = [...valores];
      let addedCount = 0;
      let updatedCount = 0;
      
      Object.entries(params).forEach(([campo, defaultValue], idx) => {
        const valorStr = typeof defaultValue === 'object' ? JSON.stringify(defaultValue) : String(defaultValue);
        
        if (existingCamposMap.has(campo)) {
          // Campo existe - actualizar valor si el actual está vacío o es diferente
          const existingIdx = existingCamposMap.get(campo);
          if (!newValores[existingIdx].valor || newValores[existingIdx].valor !== valorStr) {
            newValores[existingIdx] = {
              ...newValores[existingIdx],
              valor: valorStr
            };
            updatedCount++;
          }
        } else {
          // Campo nuevo - agregar
          newValores.push({
            campo,
            valor: valorStr,
            descripcion: '',
            orden: valores.length + idx
          });
          addedCount++;
        }
      });
      
      setValores(newValores);
      
      if (addedCount > 0 && updatedCount > 0) {
        toast.success(`${addedCount} campo(s) agregado(s), ${updatedCount} valor(es) actualizado(s)`);
      } else if (addedCount > 0) {
        toast.success(`${addedCount} campo(s) agregado(s)`);
      } else if (updatedCount > 0) {
        toast.success(`${updatedCount} valor(es) actualizado(s)`);
      } else {
        toast.info('Los valores ya están sincronizados');
      }
    } catch {
      toast.error('Error al parsear parámetros de operación');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#9d8272]" />
      </div>
    );
  }
  
  return (
    <TooltipProvider>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/integraciones')}
              className="text-[#888888]"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Volver
            </Button>
            <div>
              <h1 className="text-xl font-bold text-[#333333]">
                {isEditing ? 'Configurar Integración' : 'Nueva Integración'}
              </h1>
              {isEditing && (
                <code className="text-xs text-[#9d8272] bg-[#f0ece8] px-2 py-0.5 rounded">
                  {codigo}
                </code>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isEditing && (
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={testing}
                className="border-[#c5b8a2]"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Probar
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#9d8272] hover:bg-[#8c7161] text-white"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar
            </Button>
          </div>
        </div>
        
        {/* Formulario */}
        <div className="space-y-6">
          {/* 1. INFORMACIÓN GENERAL */}
          <Card>
            <CardHeader className="py-3 bg-[#f8f6f4]">
              <CardTitle className="text-sm font-semibold text-[#333333]">
                1. INFORMACIÓN GENERAL
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">
                    Código <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.codigo}
                    onChange={(e) => handleChange('codigo', e.target.value.toLowerCase().replace(/\s/g, '_'))}
                    placeholder="ej: enviar_notas_sperant"
                    className="border-[#c5b8a2] font-mono"
                    disabled={isEditing}
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">
                    Nombre <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="ej: Sperant - Notas Postventa"
                    className="border-[#c5b8a2]"
                  />
                </div>
                
                <div className="sm:col-span-2">
                  <Label className="text-sm font-medium">Descripción</Label>
                  <Textarea
                    value={formData.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    placeholder="Descripción de la integración..."
                    className="border-[#c5b8a2] resize-none"
                    rows={2}
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Logo URL</Label>
                  <Input
                    value={formData.logo_url}
                    onChange={(e) => handleChange('logo_url', e.target.value)}
                    placeholder="https://..."
                    className="border-[#c5b8a2]"
                  />
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.activo}
                      onCheckedChange={(checked) => handleChange('activo', checked)}
                    />
                    <Label className="text-sm">Activo</Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Orden:</Label>
                    <Input
                      type="number"
                      value={formData.orden}
                      onChange={(e) => handleChange('orden', parseInt(e.target.value) || 0)}
                      className="border-[#c5b8a2] w-20"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* 2. PARÁMETROS DE ENVÍO */}
          <Card>
            <CardHeader className="py-3 bg-[#f8f6f4]">
              <CardTitle className="text-sm font-semibold text-[#333333]">
                2. PARÁMETROS DE ENVÍO
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {/* OAuth */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1">
                    URL Token (OAuth)
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-3 h-3 text-[#888888]" />
                      </TooltipTrigger>
                      <TooltipContent>
                        Dejar vacío si usa token fijo
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <Input
                    value={formData.url_token_oauth}
                    onChange={(e) => handleChange('url_token_oauth', e.target.value)}
                    placeholder="https://api.example.com/oauth/token"
                    className="border-[#c5b8a2]"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1">
                    Token (OAuth)
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-3 h-3 text-[#888888]" />
                      </TooltipTrigger>
                      <TooltipContent>
                        Token fijo de autorización
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <div className="relative">
                    <Input
                      type={showTokenOauth ? 'text' : 'password'}
                      value={formData.token_oauth}
                      onChange={(e) => handleChange('token_oauth', e.target.value)}
                      placeholder="Token de autorización"
                      className="border-[#c5b8a2] pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2"
                      onClick={() => setShowTokenOauth(!showTokenOauth)}
                    >
                      {showTokenOauth ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* URL y Método */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="sm:col-span-3">
                  <Label className="text-sm font-medium">
                    URL API de salida <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.url_api_salida}
                    onChange={(e) => handleChange('url_api_salida', e.target.value)}
                    placeholder="https://api.example.com/endpoint/{{id}}"
                    className="border-[#c5b8a2] font-mono text-sm"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Método HTTP</Label>
                  <Select value={formData.metodo_http} onValueChange={(v) => handleChange('metodo_http', v)}>
                    <SelectTrigger className="border-[#c5b8a2]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GET">GET</SelectItem>
                      <SelectItem value="POST">POST</SelectItem>
                      <SelectItem value="PUT">PUT</SelectItem>
                      <SelectItem value="PATCH">PATCH</SelectItem>
                      <SelectItem value="DELETE">DELETE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Credenciales */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Cliente ID</Label>
                  <div className="relative">
                    <Input
                      type={showClienteId ? 'text' : 'password'}
                      value={formData.cliente_id}
                      onChange={(e) => handleChange('cliente_id', e.target.value)}
                      placeholder="ID del cliente"
                      className="border-[#c5b8a2] pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2"
                      onClick={() => setShowClienteId(!showClienteId)}
                    >
                      {showClienteId ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Secret ID</Label>
                  <div className="relative">
                    <Input
                      type={showSecretId ? 'text' : 'password'}
                      value={formData.secret_id}
                      onChange={(e) => handleChange('secret_id', e.target.value)}
                      placeholder="Secret del cliente"
                      className="border-[#c5b8a2] pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2"
                      onClick={() => setShowSecretId(!showSecretId)}
                    >
                      {showSecretId ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Timeout y Reintentos */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm font-medium">Timeout (seg)</Label>
                  <Input
                    type="number"
                    value={formData.timeout}
                    onChange={(e) => handleChange('timeout', parseInt(e.target.value) || 30)}
                    className="border-[#c5b8a2]"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Reintentos</Label>
                  <Input
                    type="number"
                    value={formData.reintentos}
                    onChange={(e) => handleChange('reintentos', parseInt(e.target.value) || 3)}
                    className="border-[#c5b8a2]"
                  />
                </div>
                
                <div className="sm:col-span-2">
                  <Label className="text-sm font-medium">Método OAuth</Label>
                  <Select value={formData.metodo_http_oauth} onValueChange={(v) => handleChange('metodo_http_oauth', v)}>
                    <SelectTrigger className="border-[#c5b8a2]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GET">GET</SelectItem>
                      <SelectItem value="POST">POST</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* JSONs */}
              <div>
                <Label className="text-sm font-medium flex items-center gap-1">
                  Parámetros de operación (JSON)
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3 text-[#888888]" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      Parámetros dinámicos con valores por defecto
                    </TooltipContent>
                  </Tooltip>
                </Label>
                <Textarea
                  value={formData.parametros_operacion}
                  onChange={(e) => handleChange('parametros_operacion', e.target.value)}
                  className={`border-[#c5b8a2] font-mono text-xs resize-none ${!isValidJson(formData.parametros_operacion) ? 'border-red-500' : ''}`}
                  rows={4}
                />
                {!isValidJson(formData.parametros_operacion) && (
                  <p className="text-xs text-red-500 mt-1">JSON inválido</p>
                )}
              </div>
              
              <div>
                <Label className="text-sm font-medium">JSON OAuth</Label>
                <Textarea
                  value={formData.json_oauth}
                  onChange={(e) => handleChange('json_oauth', e.target.value)}
                  className={`border-[#c5b8a2] font-mono text-xs resize-none ${!isValidJson(formData.json_oauth) ? 'border-red-500' : ''}`}
                  rows={4}
                  placeholder='{"grant_type": "client_credentials", "client_id": "{{cliente_id}}"}'
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">JSON Envío</Label>
                <Textarea
                  value={formData.json_envio}
                  onChange={(e) => handleChange('json_envio', e.target.value)}
                  className={`border-[#c5b8a2] font-mono text-xs resize-none ${!isValidJson(formData.json_envio) ? 'border-red-500' : ''}`}
                  rows={6}
                  placeholder='{"campo": "{{parametros.campo}}", "dato": "{{dato}}"}'
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Headers Adicionales (JSON)</Label>
                <Textarea
                  value={formData.headers_adicionales}
                  onChange={(e) => handleChange('headers_adicionales', e.target.value)}
                  className={`border-[#c5b8a2] font-mono text-xs resize-none ${!isValidJson(formData.headers_adicionales) ? 'border-red-500' : ''}`}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
          
          {/* 3. PARÁMETROS DE RECEPCIÓN */}
          <Card>
            <CardHeader className="py-3 bg-[#f8f6f4]">
              <CardTitle className="text-sm font-semibold text-[#333333]">
                3. PARÁMETROS DE RECEPCIÓN
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1">
                    URL API de entrada
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-3 h-3 text-[#888888]" />
                      </TooltipTrigger>
                      <TooltipContent>
                        Webhook para recibir datos de la plataforma externa
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <Input
                    value={formData.url_api_entrada}
                    onChange={(e) => handleChange('url_api_entrada', e.target.value)}
                    placeholder="https://mi-app.com/api/webhooks/..."
                    className="border-[#c5b8a2]"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1">
                    Token API de entrada
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-3 h-3 text-[#888888]" />
                      </TooltipTrigger>
                      <TooltipContent>
                        Token para validar requests entrantes
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <div className="relative">
                    <Input
                      type={showTokenEntrada ? 'text' : 'password'}
                      value={formData.token_api_entrada}
                      onChange={(e) => handleChange('token_api_entrada', e.target.value)}
                      placeholder="Token de validación"
                      className="border-[#c5b8a2] pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2"
                      onClick={() => setShowTokenEntrada(!showTokenEntrada)}
                    >
                      {showTokenEntrada ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* 4. VALORES DE PARÁMETROS */}
          <Card>
            <CardHeader className="py-3 bg-[#f8f6f4]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-[#333333]">
                  4. VALORES DE PARÁMETROS
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={syncValoresFromParams}
                    className="border-[#c5b8a2] text-xs"
                  >
                    Sincronizar
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addValor}
                    className="border-[#c5b8a2] text-[#9d8272]"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Agregar
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {valores.length === 0 ? (
                <div className="text-center py-6 text-[#888888]">
                  <Info className="w-8 h-8 mx-auto mb-2 text-[#c5b8a2]" />
                  <p className="text-sm">No hay valores configurados</p>
                  <p className="text-xs mt-1">Usa "Sincronizar" para crear valores desde los parámetros de operación</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {valores.map((valor, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-[#faf9f7] rounded-lg">
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-2">
                        <Input
                          value={valor.campo}
                          onChange={(e) => updateValor(idx, 'campo', e.target.value)}
                          placeholder="Campo"
                          className="border-[#c5b8a2] text-sm font-mono"
                        />
                        <Input
                          value={valor.valor}
                          onChange={(e) => updateValor(idx, 'valor', e.target.value)}
                          placeholder="Valor"
                          className="border-[#c5b8a2] text-sm"
                        />
                        <Input
                          value={valor.descripcion}
                          onChange={(e) => updateValor(idx, 'descripcion', e.target.value)}
                          placeholder="Descripción"
                          className="border-[#c5b8a2] text-sm sm:col-span-2"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeValor(idx)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default IntegracionConfig;
